import { Link } from 'react-router-dom';
import { HeartFilled } from '@ant-design/icons';
import FeatureCarousel from './onboarding/FeatureCarousel';
// Local brand logo. Vite handles the PNG import and returns the
// hashed asset URL at build time — no external URL involved.
import glassILogo from '../images/Glass-i_Logo.png';

// Left-side branding panel for the onboarding shell. The parent
// (PublicLayout shell mode) controls the column width; this
// component fills its slot and lays out:
//
//   1. glass-i brand mark
//   2. "Made for Indian Glass Processors &amp; Retailers" status badge
//   3. headline + description
//   4. auto-rotating <FeatureCarousel /> with one feature card visible
//      at a time (replaces the previous static list of three cards)
//   5. footer with Glass-i / © / Features / Privacy / Terms / Contact
//      and the "Made with ♥ for Indian glass industry" tagline
//
// Hidden below lg via the parent — mobile gets a slim brand strip
// instead. Sizes are tuned to fit a standard 1366×768 laptop
// viewport without overflow.

// Footer links. /privacy, /terms, /refund-policy, /contact are real
// public legal pages mounted outside PublicOnlyRoute and rendered via
// PublicLegalPageLayout. "Features" is still an in-page anchor since
// no marketing/features route has shipped yet.
const FOOTER_LINKS = [
  { label: 'Features', to: '#features', external: true },
  { label: 'Privacy', to: '/privacy' },
  { label: 'Terms', to: '/terms' },
  { label: 'Refund', to: '/refund-policy' },
  { label: 'Contact', to: '/contact' }
];

export default function AuthBrandPanel() {
  const year = new Date().getFullYear();
  return (
    <div className="auth-brand-panel text-white relative overflow-hidden flex w-full h-full min-w-0">
      {/* Decorative grain overlay — fine random noise (CSS-only)
          softens the otherwise-too-clean gradient and gives the
          panel a printed-poster quality on high-DPI screens. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-[0.04] mix-blend-overlay"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='180' height='180'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%25' height='100%25' filter='url(%23n)' opacity='0.9'/></svg>\")"
        }}
      />
      <div className="relative z-10 flex flex-col justify-between px-8 xl:px-10 py-8 xl:py-10 w-full min-w-0">
        {/* TOP — brand + badge + headline + description */}
        <div className="min-w-0">
          {/* Brand — local logo asset replaces the previous
              "g" tile + "glass-i" wordmark. */}
          <img
            src={glassILogo}
            alt="Glass-i"
            className="block h-24 w-auto object-contain max-w-[440px]"
          />

          {/* Status badge */}
          <div className="mt-6 inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 backdrop-blur-sm px-3 py-1.5">
            <span
              className="relative flex h-2 w-2 shrink-0"
              aria-hidden="true"
            >
              <span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping opacity-60" />
              <span className="relative h-2 w-2 rounded-full bg-emerald-400" />
            </span>
            <span className="text-xs font-medium text-white/90 tracking-wide whitespace-nowrap">
              Made for Indian Glass Processors &amp; Retailers
            </span>
          </div>

          {/* Headline — solid white first line, lighter second line.
              Slight negative letter-spacing on the display copy and a
              soft drop-shadow give the text a refined-print feel
              against the gradient backdrop. */}
          <h1
            className="mt-6 text-3xl xl:text-4xl 2xl:text-5xl font-bold leading-[1.05] tracking-[-0.02em] m-0"
            style={{ textShadow: '0 1px 0 rgba(15,23,42,0.18)' }}
          >
            Built for Glass.
            <br />
            <span className="text-white/75">Built for India.</span>
          </h1>

          {/* Description */}
          <p className="mt-5 text-sm xl:text-base text-white/80 max-w-xl leading-relaxed m-0">
            The complete ERP for MSME glass processors — from cutting orders
            to delivery challan, all in one platform.
          </p>
        </div>

        {/* MIDDLE — auto-rotating feature carousel. Replaces the
            previous static list of three stacked cards. Uses the
            same translucent card styling so the visual unit is
            unchanged; only one card renders at a time. */}
        <div className="mt-8 min-w-0">
          <FeatureCarousel />
        </div>

        {/* FOOTER — Glass-i / © / link row + heart tagline */}
        <div className="mt-6 pt-4 border-t border-white/15 min-w-0">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-white/70">
            <span className="font-semibold text-white">Glass-i</span>
            <span>© {year}</span>
            {FOOTER_LINKS.map((l) =>
              l.external ? (
                <a
                  key={l.label}
                  href={l.to}
                  className="hover:text-white transition-colors"
                >
                  {l.label}
                </a>
              ) : (
                <Link
                  key={l.label}
                  to={l.to}
                  className="hover:text-white transition-colors"
                >
                  {l.label}
                </Link>
              )
            )}
          </div>
          <p className="text-xs text-white/65 m-0 mt-2 inline-flex items-center gap-1.5">
            Made with{' '}
            <HeartFilled
              className="text-rose-400 text-[11px]"
              aria-label="love"
            />{' '}
            for Indian glass industry
          </p>
        </div>
      </div>
    </div>
  );
}
